import type { ReactNode } from "react";
import { IOS_STORE_URL, PLAY_STORE_URL } from "@/lib/native/openInAppLink";
import { useTheme } from "@/context/ThemeContext";

type StoreBadgeProps = {
  href: string;
  label: string;
  storeName: string;
  variant: "apple" | "google";
  children: ReactNode;
};

const lightVariantStyles = {
  apple: [
    "border-teal-400/50 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-900 text-white",
    "shadow-[0_4px_22px_-4px_rgba(45,212,191,0.55),0_2px_8px_-2px_rgba(0,0,0,0.25)]",
    "hover:border-teal-300/75 hover:from-teal-500 hover:via-teal-600 hover:to-teal-900",
    "hover:shadow-[0_8px_30px_-4px_rgba(45,212,191,0.65),0_4px_12px_-2px_rgba(0,0,0,0.2)]",
  ].join(" "),
  google: [
    "border-sky-400/45 bg-gradient-to-br from-sky-700 via-cyan-800 to-slate-900 text-white",
    "shadow-[0_4px_22px_-4px_rgba(56,189,248,0.5),0_2px_8px_-2px_rgba(0,0,0,0.25)]",
    "hover:border-sky-300/70 hover:from-sky-600 hover:via-cyan-700 hover:to-slate-900",
    "hover:shadow-[0_8px_30px_-4px_rgba(56,189,248,0.6),0_4px_12px_-2px_rgba(0,0,0,0.2)]",
  ].join(" "),
};

const darkVariantStyles = {
  apple: [
    "border-teal-500/30 bg-gradient-to-br from-teal-800 via-teal-900 to-teal-950 text-white",
    "shadow-[0_4px_16px_-6px_rgba(45,212,191,0.2),0_2px_6px_-2px_rgba(0,0,0,0.35)]",
    "hover:border-teal-400/40 hover:from-teal-700 hover:via-teal-800 hover:to-teal-950",
    "hover:shadow-[0_6px_22px_-6px_rgba(45,212,191,0.26),0_2px_8px_-2px_rgba(0,0,0,0.3)]",
  ].join(" "),
  google: [
    "border-sky-500/28 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 text-white",
    "shadow-[0_4px_16px_-6px_rgba(56,189,248,0.16),0_2px_6px_-2px_rgba(0,0,0,0.35)]",
    "hover:border-sky-400/38 hover:from-slate-700 hover:via-slate-800 hover:to-slate-950",
    "hover:shadow-[0_6px_22px_-6px_rgba(56,189,248,0.22),0_2px_8px_-2px_rgba(0,0,0,0.3)]",
  ].join(" "),
};

const lightLabelStyles = {
  apple: "text-white/85 group-hover:text-white",
  google: "text-white/85 group-hover:text-white",
};

const darkLabelStyles = {
  apple: "text-teal-100/55 group-hover:text-teal-100/70",
  google: "text-sky-100/55 group-hover:text-sky-100/70",
};

function StoreBadge({
  href,
  label,
  storeName,
  variant,
  forceBright,
  children,
}: StoreBadgeProps & { forceBright?: boolean }) {
  const { theme } = useTheme();
  const isDark = !forceBright && theme === "dark";
  const buttonStyles = isDark
    ? darkVariantStyles[variant]
    : lightVariantStyles[variant];
  const labelStyles = isDark
    ? darkLabelStyles[variant]
    : lightLabelStyles[variant];

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`group flex h-11 w-full min-w-0 items-center justify-center gap-2.5 rounded-xl border px-3 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50 sm:inline-flex sm:w-auto sm:min-w-[8.75rem] sm:justify-start ${buttonStyles}`}
      aria-label={`${label} ${storeName}`}
    >
      {children}
      <span className="flex flex-col leading-none">
        <span
          className={`text-[9px] font-medium uppercase tracking-wide ${labelStyles}`}
        >
          {label}
        </span>
        <span className="text-sm font-semibold tracking-tight text-white">
          {storeName}
        </span>
      </span>
    </a>
  );
}

function AppleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6 shrink-0 fill-current"
      aria-hidden
    >
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function GooglePlayIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0" aria-hidden>
      <path
        fill="#4285F4"
        d="M3 20.5V3.5C3 2.91 3.34 2.39 3.84 2.15L13.69 12 3.84 21.85C3.34 21.6 3 21.09 3 20.5Z"
      />
      <path
        fill="#34A853"
        d="M16.81 15.12 6.05 21.34 14.54 12.85 16.81 15.12Z"
      />
      <path
        fill="#FBBC04"
        d="M16.81 8.88 14.54 11.15 6.05 2.66 16.81 8.88Z"
      />
      <path
        fill="#EA4335"
        d="M17.14 15.45 22.58 12.18c.5-.32.5-1.04 0-1.36l-5.44-3.27-2.6 3.6 2.6 3.6Z"
      />
    </svg>
  );
}

type AppStoreBadgesProps = {
  appStoreLabel: string;
  appStoreName: string;
  playStoreLabel: string;
  playStoreName: string;
  /** Use vivid teal/blue badges (e.g. on dark app-download sheet). */
  forceBright?: boolean;
};

export function AppStoreBadges({
  appStoreLabel,
  appStoreName,
  playStoreLabel,
  playStoreName,
  forceBright = false,
}: AppStoreBadgesProps) {
  return (
    <div className="grid w-full grid-cols-2 gap-2.5 sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:gap-3">
      <StoreBadge
        href={IOS_STORE_URL}
        label={appStoreLabel}
        storeName={appStoreName}
        variant="apple"
        forceBright={forceBright}
      >
        <AppleIcon />
      </StoreBadge>
      <StoreBadge
        href={PLAY_STORE_URL}
        label={playStoreLabel}
        storeName={playStoreName}
        variant="google"
        forceBright={forceBright}
      >
        <GooglePlayIcon />
      </StoreBadge>
    </div>
  );
}
